import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import HeaderV2 from '../../components/headerV2/HeaderV2';
import FooterV2 from '../../components/footerV2/FooterV2';
import AboutHero from '../../components/aboutHero/AboutHero';
import useScrollReveal from '../../hooks/useScrollReveal';

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";

import { FOUNDERS, TEAM } from './leadershipData';
import Petals from './Petals';
import './aboutV2.css';

/* Team photo supplied in the Sep 2026 website review (lives in /public). */
const LeadershipImg = process.env.PUBLIC_URL + "/images/about-v2/leadership-team.jpg";

const scrollTop = () => window.scrollTo(0, 0);

const LeadershipTeam = ({ path }) => {

    // Setting metadata and schemadata
    const [metadata, setMetadata] = useState({ title: "JJFIndia | About us" });
    const [schemadata, setSchemadata] = useState({});

    const rootRef = useRef(null);
    useScrollReveal(rootRef);

    // Getting the schemadata and setting the schema data.
    useEffect(() => {
        let tempMetadata = metadataJSON[path];
        let tempSchemadata = schemadataJSON[path];

        if (tempMetadata) {
            setMetadata(tempMetadata);
        } else {
            tempMetadata = {
                title: "JJFIndia | About us",
                description: "JJFIndia About us page",
                keywords: "",
                robots: "index, follow",
                canonical: "https://jjfindia.com/about-us/leadership-team",
                openGraph: {
                    title: "JJFIndia | About us",
                    description: "JJFIndia About us page",
                    url: "https://jjfindia.com/about-us/leadership-team"
                },
                twitter: {
                    card: "summary_large_image",
                    title: "JJFIndia | About us",
                    description: "JJFIndia About us page"
                }
            }

            setMetadata(tempMetadata);
        }

        if (tempSchemadata) {
            setSchemadata(tempSchemadata);
        }

    }, [path]);

    return (
        <Fragment>
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
                    rel="stylesheet"
                />
                <title>{metadata.title}</title>
                <meta name="description" content={metadata.description} />
                <meta name="keywords" content={metadata.keywords} />
                <meta name="robots" content={metadata.robots} />
                <link rel="canonical" href={metadata.canonical} />
                <meta property="og:title" content={metadata.openGraph?.title} />
                <meta property="og:description" content={metadata.openGraph?.description} />
                <meta property="og:url" content={metadata.canonical} />
                <meta name="twitter:title" content={metadata.twitter?.title} />
                <meta name="twitter:description" content={metadata.twitter?.description} />
                <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
            </Helmet>

            <div className="jjfv2 about-live" ref={rootRef}>
                <HeaderV2 />

                <main className="page_content">
                    {/* ---------- Page hero ---------- */}
                    <AboutHero
                        eyebrow="The people behind the mission"
                        title="Leadership Team"
                        crumbs={[
                            { label: 'Home', to: '/' },
                            { label: 'About Us', to: '/about-us/who-we-are' },
                            { label: 'Leadership Team' },
                        ]}
                    />

                    {/* ---------- Our leadership (intro) ---------- */}
                    <section className="section about" id="leadership-intro" style={{ paddingTop: "36px", paddingBottom: "36px" }}>
                        <div className="wrap split" style={{ position: "relative", zIndex: 1 }}>
                            <div className="about-media reveal">
                                <span className="leaf-frame" aria-hidden="true"></span>
                                <div className="ph">
                                    <img src={LeadershipImg} alt="Jainam Jivika Foundation leadership team with Life Saver Army volunteers" loading="lazy" />
                                </div>
                            </div>
                            <div className="about-copy reveal d1">
                                <span className="eyebrow">Leadership Team</span>
                                <h2>The People Behind the Work</h2>
                                <p className="lead">
                                    JJF is guided by a founding team and a leadership group that stays close to
                                    the ground, bringing research, technology and community organising to the
                                    Foundation's work. Meet the people who set our direction and often show up at
                                    the camps themselves.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* ---------- Leadership & visionaries ---------- */}
                    <section className="section section--mist lead-section">
                        {/* subtle falling petals / confetti backdrop */}
                        <Petals />

                        <div className="wrap">
                            <div className="about-head">
                                <span className="eyebrow">Leadership Philosophy</span>
                                <h2>Our Leaders Speak</h2>
                            </div>

                            {/* big founder photo cards */}
                            <div className="lead-feature">
                                {FOUNDERS.map((f, i) => (
                                    <Link
                                        className={"lfc reveal" + (i ? " d1" : "")}
                                        to={`/about-us/leadership-team/${f.slug}`}
                                        onClick={scrollTop}
                                        aria-label={`View profile of ${f.name}`}
                                        key={i}
                                    >
                                        <img src={f.img} alt={f.name} loading="lazy" />
                                        {/* geometric corner ornament */}
                                        <svg className="lfc__corner" viewBox="0 0 72 72" fill="none" aria-hidden="true">
                                            <path d="M71 1H34M71 1v33" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            <path d="M53 9l8 8-8 8-8-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                        </svg>
                                        <span className="lfc__tag">
                                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01z" /></svg>
                                            {f.tag}
                                        </span>
                                        <div className="lfc__body">
                                            <h3 className="lfc__name">{f.name}</h3>
                                            <span className="lfc__role">{f.role}</span>
                                            <div className="lfc__line"></div>
                                            <span className="lfc__more">View Profile
                                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {/* small team cards */}
                            <h3 className="lead-subhead">Our Team of Experts</h3>
                            <div className="lead-team">
                                {TEAM.map((t, i) => (
                                    <Link
                                        className={"ltc reveal" + (i ? " d" + i : "")}
                                        to={`/about-us/leadership-team/${t.slug}`}
                                        onClick={scrollTop}
                                        aria-label={`View profile of ${t.name}`}
                                        key={i}
                                    >
                                        <div className="ltc__avatar">
                                            <img src={t.img} alt={t.name} loading="lazy" />
                                        </div>
                                        <h3 className="ltc__name">{t.name}</h3>
                                        <span className="ltc__role">{t.role}</span>
                                        <p className="ltc__bio">{t.bio}</p>
                                        <span className="ltc__more" aria-hidden="true">
                                            <span className="ltc__more-text">View Profile</span>
                                            <svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                </main>

                <FooterV2 />
            </div>
        </Fragment>
    )
};

export default LeadershipTeam;
